/**
 * RBAC Caching Service
 *
 * Centralized service for fetching and caching RBAC (Role-Based Access Control) data.
 * This prevents redundant API calls across different components.
 *
 * Features:
 * - In-memory cache with TTL (Time To Live)
 * - localStorage persistence for offline support
 * - Automatic cache invalidation after 15 minutes
 * - Single source of truth for RBAC data
 */

const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds
const STORAGE_KEY_ROLE = 'rbac_role';
const STORAGE_KEY_DATA = 'rbac_data';
const STORAGE_KEY_TIMESTAMP = 'rbac_cache_timestamp';

// In-memory cache
let cachedRBACData = null;
let cacheTimestamp = null;

/**
 * Check if the cache is still valid
 */
const isCacheValid = () => {
  if (!cachedRBACData || !cacheTimestamp) {
    return false;
  }

  const now = Date.now();
  const age = now - cacheTimestamp;

  return age < CACHE_DURATION;
};

/**
 * Load RBAC data from localStorage
 */
const loadFromLocalStorage = () => {
  try {
    const storedRole = localStorage.getItem(STORAGE_KEY_ROLE);
    const storedData = localStorage.getItem(STORAGE_KEY_DATA);
    const storedTimestamp = localStorage.getItem(STORAGE_KEY_TIMESTAMP);

    if (storedRole && storedData && storedTimestamp) {
      const timestamp = parseInt(storedTimestamp, 10);
      const now = Date.now();

      // Check if localStorage cache is still valid
      if (now - timestamp < CACHE_DURATION) {
        return {
          global_role: storedRole,
          business_roles: JSON.parse(storedData),
          fromCache: true
        };
      }
    }
  } catch (error) {
    console.error('Error loading RBAC from localStorage:', error);
  }

  return null;
};

/**
 * Save RBAC data to localStorage
 */
const saveToLocalStorage = (rbacData) => {
  try {
    localStorage.setItem(STORAGE_KEY_ROLE, rbacData.global_role);
    localStorage.setItem(STORAGE_KEY_DATA, JSON.stringify(rbacData.business_roles));
    localStorage.setItem(STORAGE_KEY_TIMESTAMP, Date.now().toString());
  } catch (error) {
    console.error('Error saving RBAC to localStorage:', error);
  }
};

/**
 * Fetch RBAC data with caching
 *
 * @param {string} email - User email
 * @param {boolean} forceRefresh - Force a fresh fetch, ignoring cache
 * @returns {Promise<Object>} RBAC data object with global_role and business_roles
 */
export const fetchRBACWithCache = async (email, forceRefresh = false) => {
  // Return cached data if valid and not forcing refresh
  if (!forceRefresh && isCacheValid()) {
    // console.log('✅ Using in-memory RBAC cache');
    return { ...cachedRBACData, fromCache: true };
  }

  // Try localStorage cache
  if (!forceRefresh) {
    const localStorageData = loadFromLocalStorage();
    if (localStorageData) {
      // console.log('✅ Using localStorage RBAC cache');
      // Update in-memory cache
      cachedRBACData = localStorageData;
      cacheTimestamp = Date.now();
      return localStorageData;
    }
  }

  // Fetch fresh data from API
  try {
    // console.log('🔄 Fetching fresh RBAC data from API...');
    const url = process.env.REACT_APP_API_BASE_URL + '/fetch_rbac' || 'http://127.0.0.1:6532/fetch_rbac';

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user: email }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const rbacData = await response.json();

    // Update caches
    cachedRBACData = rbacData;
    cacheTimestamp = Date.now();
    saveToLocalStorage(rbacData);

    // console.log('✅ RBAC data fetched and cached successfully');
    return { ...rbacData, fromCache: false };

  } catch (error) {
    console.error('❌ Error fetching RBAC data:', error);

    // Fallback to localStorage even if expired
    try {
      const storedRole = localStorage.getItem(STORAGE_KEY_ROLE) || '';
      const storedData = localStorage.getItem(STORAGE_KEY_DATA);
      const businessRoles = storedData ? JSON.parse(storedData) : [];

      const fallbackData = {
        global_role: storedRole,
        business_roles: businessRoles,
        fromCache: true,
        isStale: true
      };

      // console.log('⚠️ Using stale RBAC data from localStorage as fallback');
      return fallbackData;
    } catch (fallbackError) {
      console.error('❌ Error loading fallback RBAC data:', fallbackError);
      throw new Error('Unable to load RBAC data');
    }
  }
};

/**
 * Clear the RBAC cache (useful on logout)
 */
export const clearRBACCache = () => {
  cachedRBACData = null;
  cacheTimestamp = null;

  try {
    localStorage.removeItem(STORAGE_KEY_ROLE);
    localStorage.removeItem(STORAGE_KEY_DATA);
    localStorage.removeItem(STORAGE_KEY_TIMESTAMP);
    // console.log('✅ RBAC cache cleared');
  } catch (error) {
    console.error('❌ Error clearing RBAC cache:', error);
  }
};

/**
 * Get cached RBAC data without fetching (returns null if no cache)
 */
export const getCachedRBAC = () => {
  if (isCacheValid()) {
    return { ...cachedRBACData, fromCache: true };
  }

  const localStorageData = loadFromLocalStorage();
  if (localStorageData) {
    cachedRBACData = localStorageData;
    cacheTimestamp = Date.now();
    return localStorageData;
  }

  return null;
};
